import React, { useState } from "react";
import { Activity } from "@ofqual/portal-activity-feed";
import { Organisation } from "../../domain/Organisation";
import { ActivityFeed } from "@ofqual/portal-activity-feed";
import { buildDefinitions } from "./buildDefinitions";
import { EntityDialog, DialogType } from "./types";
import { RenderDialog } from "./RenderDialog";
import { useIsAdmin, useIsOfqualAdmin } from "../../hooks/useIsAdmin";
import { UserRole } from "../../domain/userRole";
import { useAuth } from "@ofqual/portal-core/core/hooks/useAuth";
import { authSelector } from "../../redux/selectors";

type OrganisationsActivityFeedProps = {
  organisation: Organisation;
};

export const OrganisationsActivityFeed: React.FC<OrganisationsActivityFeedProps> = ({
  organisation,
}) => {
  const isAdmin = useIsAdmin();
  const isOfqualAdmin = useIsOfqualAdmin();

  const [focussedEntity, setFocussedEntity] = useState<EntityDialog | null>(
    null
  );

  const { isInRole } = useAuth(authSelector);
  const canRecordCompletion = isInRole(
    UserRole.ITAdmin,
    UserRole.OfqualRecognitionAdmin
  );
  const canCreateEvent = isInRole(
    UserRole.OfqualRecognitionAdmin,
    UserRole.OfqualRecognitionUser,
    UserRole.ITAdmin
  );

  const defs = buildDefinitions(
    (activity: Activity) => {
      setFocussedEntity({
        dialogType: DialogType.ActivityAction,
        focussedActivity: activity,
      });
    },
    organisation,
    canRecordCompletion
  );

  const getPermissionedActions = () => {
    const actions = [
      {
        label: isOfqualAdmin
          ? "Review pending changes"
          : "Show changes pending approval",
        disabled: !organisation.organisationChanges.length,
        dataTest: "review-changes-button",
        action: () =>
          setFocussedEntity({
            dialogType: DialogType.Approval,
          }),
      },
      {
        label: "Send Message",
        dataTest: "ao-send-message",
        action: () =>
          setFocussedEntity({
            dialogType: DialogType.SendMessage,
          }),
      },
      {
        label: "Add Attachments",
        dataTest: "ao-add-attachment",
        action: () =>
          setFocussedEntity({
            dialogType: DialogType.AddAttachment,
          }),
      },
    ];
    if (canCreateEvent) {
      const addEventAction = {
        label: "Add event",
        disabled: !canCreateEvent,
        dataTest: "add-event-button",
        action: () =>
          setFocussedEntity({
            dialogType: DialogType.AddEvent,
          }),
      };
      actions.splice(1, 0, addEventAction);
    }
    return actions;
  };

  return (
    <>
      <ActivityFeed
        resourceId={organisation?.id}
        specialSegmentLabel={(activity) =>
          activity.activityType === 25 ? "Events" : null
        }
        definitions={defs}
        actions={getPermissionedActions()}
      />
      <RenderDialog
        focussedEntity={focussedEntity}
        onClose={() => setFocussedEntity(null)}
        organisation={organisation}
        isAdmin={isAdmin}
        isOfqualAdmin={isOfqualAdmin}
      />
    </>
  );
};
